import { FC } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Box, Stack } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import * as Layout from 'layouts/ContentLayout';

import BackButton from '../../../../components/BackButton';
import * as bankRoutes from '../../../Bank/routes';
import * as routes from '../../routes';
import RecurringExportsList from './components/RecurringExportsList';
import useGetRecurringExports from './hooks/useGetRecurringExports';
import locales from './locales';

const Home: FC = () => {
  const { companyProfileId = '' } = useCompanyContext();
  const { state } = useLocation<{ fromBankOverview: boolean }>();
  const isFromBankOverview = !!state?.fromBankOverview;

  const {
    error,
    loading,
    recurringExportsItems,
    refetch: refetchExports,
  } = useGetRecurringExports(companyProfileId);

  const canRenderExports =
    !loading &&
    !error &&
    recurringExportsItems &&
    recurringExportsItems.length > 0;

  return (
    <Layout.Root>
      <Layout.Body>
        <Stack marginBottom="space-40" spacing="space-8">
          <Stack spacing="space-32">
            {isFromBankOverview ? (
              <BackButton to={bankRoutes.bankOverview} />
            ) : null}
            <Heading size="lg">{locales.title}</Heading>
          </Stack>
          <Text size="lg">{locales.description}</Text>
        </Stack>

        <Stack spacing="space-32">
          <Box>
            <Text marginBottom="space-8" variant="light">
              {locales.newExport.sectionTitle}
            </Text>

            <SunshineCard.Group>
              <SunshineCard
                alignItems="center"
                as={Link}
                title={locales.newExport.monthly.title}
                to={routes.newExportRecurring}
              >
                <SunshineCard.Slot name="aside">
                  <SquircleIcon name="calendar" />
                </SunshineCard.Slot>

                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>

              <SunshineCard
                alignItems="center"
                as={Link}
                title={locales.newExport.oneTime.title}
                to={routes.newExportOneOff}
              >
                <SunshineCard.Slot name="aside">
                  <SquircleIcon name="export" />
                </SunshineCard.Slot>

                <SunshineCard.Slot name="action">
                  <SunshineCard.SunshineIcon name="chevron-right" />
                </SunshineCard.Slot>
              </SunshineCard>
            </SunshineCard.Group>
          </Box>

          {recurringExportsItems.length === 0 ? null : (
            <Box>
              <Text variant="light">{locales.currentExports.sectionTitle}</Text>

              {error ? <ErrorCard retry={refetchExports} /> : null}
              {loading ? <Loader /> : null}

              {canRenderExports ? (
                <RecurringExportsList exports={recurringExportsItems} />
              ) : null}
            </Box>
          )}

          <Box>
            <Text marginBottom="space-8" variant="light">
              {locales.bankStatements.sectionTitle}
            </Text>

            <SunshineCard
              alignItems="center"
              as={Link}
              title={locales.bankStatements.title}
              to={routes.documents}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="misc" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          </Box>
        </Stack>
      </Layout.Body>
    </Layout.Root>
  );
};

export default Home;
